
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { UserWithDocuments } from "@/hooks/admin/identity-docs/useSubmittedDocuments";
import { UserDocumentsList } from "./UserDocumentsList";
import { Badge } from "@/components/ui/badge";
import { Mail } from "lucide-react";

interface UserGroupedDocumentsProps {
  userGroups: UserWithDocuments[];
  onDocumentUpdated: () => void;
}

export function UserGroupedDocuments({ userGroups, onDocumentUpdated }: UserGroupedDocumentsProps) {
  if (userGroups.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">No documents found matching the filter criteria.</div>
    );
  }

  const getUserVerificationStatus = (user: UserWithDocuments) => {
    if (user.documents.some(doc => doc.verification_status === 'verified')) {
      return 'verified';
    }
    if (user.documents.some(doc => doc.verification_status === 'pending')) {
      return 'pending';
    }
    return 'rejected';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'verified':
        return <Badge className="bg-green-500">Verified</Badge>;
      case 'pending':
        return <Badge variant="outline">Pending</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return null;
    }
  };

  return (
    <Accordion type="multiple" className="space-y-4">
      {userGroups.map((user) => {
        const status = getUserVerificationStatus(user);
        const pendingCount = user.documents.filter(d => d.verification_status === 'pending').length;
        const verifiedCount = user.documents.filter(d => d.verification_status === 'verified').length;
        const rejectedCount = user.documents.filter(d => d.verification_status === 'rejected').length;
        
        return (
          <AccordionItem key={user.user_id} value={user.user_id} className="border rounded-lg">
            <AccordionTrigger className="px-4 py-3 hover:bg-muted/50 rounded-t-lg">
              <div className="flex flex-col md:flex-row md:items-center justify-between w-full text-left">
                <div>
                  <p className="font-medium">{user.name}</p>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Mail className="h-3 w-3 mr-1" />
                    {user.email}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2 md:mt-0">
                  {getStatusBadge(status)}
                  <div className="text-xs text-muted-foreground ml-2">
                    <span>{user.documents.length} documents</span>
                    {pendingCount > 0 && <span className="ml-2">{pendingCount} pending</span>}
                  </div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-3">
              <UserDocumentsList documents={user.documents} onDocumentUpdated={onDocumentUpdated} />
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
